import { TUserSelectOption } from '../components/Inputs/admin/UserListDropdown';
import { TSelectOption } from '../pages/V2/LessonHistory/Tabs/CompletedTab';
import { IClass } from '../store/BookmarkSlice';
import { TTheme } from '../store/ConfigSlice';
import { IUser } from '../store/UserSlice';

export type AdminCoursesFiltersData = {
  searchText: string;
  filterSelections: {
    selectedCurriculum?: TSelectOption | null;
    selectedSubject?: TSelectOption | null;
    selectedGrade?: TSelectOption | null;
    selectedStudent?: TUserSelectOption | null;
    selectedTutor?: TUserSelectOption | null;
    status?: TSelectOption | null;
    mode?: TSelectOption | null;
  };
  isFiltersVisible?: boolean;
};

const COOKIE_NAMES = {
  AUTH_TOKEN: 'tutopiya_auth_token',
  AUTH_USER: 'tutopiya_auth_user',
  TENANT_KEY: 'tutopiya_tenant_key',
} as const;

function setCookie(name: string, value: string, days = 7): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(
    value
  )};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

function removeCookie(name: string): void {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

// GET FROM LOCAL STORAGE
export const getAppThemeModeFromLocalStorage = (): TTheme | undefined => {
  const data = localStorage.getItem('TUTOPIYA_QUIZ_APP_THEME_MODE');
  const mode: TTheme = (data as TTheme) || 'light';
  return mode.trim() as TTheme;
};

export const getAuthTokenFromLocalStorage = (): string | undefined => {
  const data = localStorage.getItem('TUTOPIYA_QUIZ_AUTH_TOKEN');
  if (data) {
    return data;
  }
  return undefined;
};

export const getAuthUserFromLocalStorage = (): IUser | undefined => {
  const data = localStorage.getItem('TUTOPIYA_QUIZ_AUTH_USER');
  if (data) {
    return JSON.parse(data) as IUser;
  }
  return undefined;
};

export const getUserMapperFromLocalStorage = (): IUser[] | undefined => {
  const data = localStorage.getItem('TUTOPIYA_USER_MAPPER');
  if (data) {
    return JSON.parse(data) as IUser[];
  }
  return undefined;
};

export const getClassFromLocalStorage = (): IClass | undefined => {
  const data = localStorage.getItem('TUTOPIYA_QUIZ_CLASS');
  if (data) {
    return JSON.parse(data) as IClass;
  }
  return undefined;
};

export const getPDFThemeModeFromLocalStorage = (): TTheme | undefined => {
  const data = localStorage.getItem('TUTOPIYA_PDF_THEME_MODE');
  const mode: TTheme = (data as TTheme) || 'light';
  return mode.trim() as TTheme;
};

export const getAdminPortalEditModeValueFromLocalStorage = ():
  | string
  | undefined => {
  const value = localStorage.getItem('TUTOPIYA_ADMIN_EDIT_MODE_VALUE');

  return value || undefined;
};

export const getSlideViewsLimitFromLocalStorage = (): string | undefined => {
  const data = localStorage.getItem('TUTOPIYA_SLIDE_VIEWS_LIMIT');
  if (data) {
    return data;
  }
  return undefined;
};

export const getLeadModalOpenFromLocalStorage = (): boolean | undefined => {
  const data = localStorage.getItem('TUTOPIYA_LEAD_MODAL_OPEN');
  if (data) {
    return JSON.parse(data) as boolean;
  }
  return undefined;
};

export const getReleaseNoteShowedInHomeFromLocalStorage = ():
  | string
  | undefined => {
  const data = localStorage.getItem(
    'TUTOPIYA_RELEASE_NOTE_FOR_HOME_PAGE_SHOWED'
  );
  if (data) {
    return data;
  }
  return undefined;
};

export const getReleaseNoteShowedInSubjectPageFromLocalStorage = ():
  | string
  | undefined => {
  const data = localStorage.getItem(
    'TUTOPIYA_RELEASE_NOTE_FOR_SUBJECT_PAGE_SHOWED'
  );
  if (data) {
    return data;
  }
  return undefined;
};

export const getProductValueForSubTopicShowedFromLocalStorage = ():
  | string
  | undefined => {
  const value = localStorage.getItem(
    'TUTOPIYA_PRODUCT_VALUE_FOR_SUBTOPIC_SHOWED'
  );

  return value || undefined;
};

export const getProductValueForQuizShowedFromLocalStorage = ():
  | string
  | undefined => {
  const value = localStorage.getItem('TUTOPIYA_PRODUCT_VALUE_FOR_QUIZ_SHOWED');

  return value || undefined;
};

export const getBannerStatusesFromLocalStorage = (): Record<
  string,
  boolean
> => {
  const data = localStorage.getItem('TUTOPIYA_BANNER_STATUS');
  return data ? JSON.parse(data) : {};
};

export const getPdfTriggerMessageStatusFromLocalStorage = (): Record<
  string,
  boolean
> => {
  const data = localStorage.getItem('TUTOPIYA_PDF_TRIGGER_MESSAGE_STATUS');
  return data ? JSON.parse(data) : {};
};

export const getMotivationalNudgeStatusFromLocalStorage = (): Record<
  string,
  boolean
> => {
  const data = localStorage.getItem('TUTOPIYA_MOTIVATIONAL_NUDGE_STATUS');
  return data ? JSON.parse(data) : {};
};

export const getSubTopicStatusFromLocalStorage = (): Record<
  string,
  boolean
> => {
  const data = localStorage.getItem('TUTOPIYA_SUB_TOPIC_STATUS');
  return data ? JSON.parse(data) : {};
};

export const getPracticeQuestionStatusFromLocalStorage = (): Record<
  string,
  boolean
> => {
  const data = localStorage.getItem('TUTOPIYA_PRACTICE_QUESTION_STATUS');
  return data ? JSON.parse(data) : {};
};

export const getDashboardSelectedCurriculumFromLocalStorage = ():
  | string
  | undefined => {
  const value = localStorage.getItem('TUTOPIYA_DASHBOARD_SELECTED_CURRICULUM');

  return value || undefined;
};

export const getOnboardingUserIdFromLocalStorage = (): string | undefined => {
  const value = localStorage.getItem('TUTOPIYA_ONBOARDING_USER_ID');

  return value || undefined;
};

export const getTutorEditModeFromLocalStorage = (): boolean => {
  const data = localStorage.getItem('TUTOPIYA_TUTOR_EDIT_MODE');
  if (data) {
    return JSON.parse(data) as boolean;
  }
  return false;
};

export const getFeedbackModalCancelledFromLocalStorage = ():
  | string
  | undefined => {
  const value = localStorage.getItem('TUTOPIYA_FEEDBACK_MODAL_CANCELLED');
  return value || undefined;
};

export const getAdminCoursesFiltersFromLocalStorage = (
  isClasses: boolean
): AdminCoursesFiltersData | undefined => {
  const key = isClasses
    ? 'TUTOPIYA_ADMIN_CLASSES_FILTERS'
    : 'TUTOPIYA_ADMIN_COURSES_FILTERS';
  const data = localStorage.getItem(key);
  if (data) {
    return JSON.parse(data) as AdminCoursesFiltersData;
  }
  return undefined;
};

export const setTenantCookie = (tenant: string): void => {
  setCookie(COOKIE_NAMES.TENANT_KEY, tenant, 365);
};

export type CourseCreditReminderData = {
  courseId: string;
  tutorId: string;
  timestamp: number;
};

export const getLastCourseCreditReminderNotificationSentAtFromLocalStorage = (): CourseCreditReminderData[] => {
  const data = localStorage.getItem(
    'LAST_COURSE_CREDIT_REMINDER_NOTIFICATION_SENT_AT'
  );
  return data ? JSON.parse(data) : [];
};

// SET TO LOCAL STORAGE
export const setAuthTokenToLocalStorage = (token: string): void => {
  localStorage.setItem('TUTOPIYA_QUIZ_AUTH_TOKEN', token);
  setCookie(COOKIE_NAMES.AUTH_TOKEN, token, 365);
};

export const setAuthUserToLocalStorage = (user: IUser): void => {
  const currentData = getAuthUserFromLocalStorage();
  localStorage.setItem(
    'TUTOPIYA_QUIZ_AUTH_USER',
    JSON.stringify({ ...currentData, ...user })
  );

  // Store minimal user info in cookie (to avoid cookie size limits)
  const minimalUser = {
    _id: user._id,
    firstname: user.firstname,
    lastname: user.lastname,
    email: user.email,
    role: user.role,
  };

  setCookie(COOKIE_NAMES.AUTH_USER, JSON.stringify(minimalUser), 365);
};

export const setUserMapperToLocalStorage = (users: IUser[]): void => {
  localStorage.setItem('TUTOPIYA_USER_MAPPER', JSON.stringify(users));
};

export const setAppThemeModeToLocalStorage = (mode: TTheme): void => {
  localStorage.setItem('TUTOPIYA_QUIZ_APP_THEME_MODE', mode as string);
};

export const setClassToLocalStorage = (bookmark: IClass): void => {
  localStorage.setItem('TUTOPIYA_QUIZ_CLASS', JSON.stringify(bookmark));
};

export const setPDFThemeModeToLocalStorage = (mode: TTheme): void => {
  localStorage.setItem('TUTOPIYA_PDF_THEME_MODE', mode as string);
};

export const setAdminPortalEditModeValueToLocalStorage = (
  value: string
): void => {
  localStorage.setItem('TUTOPIYA_ADMIN_EDIT_MODE_VALUE', value);
};

export const setSlideViewsLimitToLocalStorage = (pageNo: string): void => {
  localStorage.setItem('TUTOPIYA_SLIDE_VIEWS_LIMIT', pageNo);
};

export const setProductValueForSubTopicShowedToLocalStorage = (
  value: string
): void => {
  localStorage.setItem('TUTOPIYA_PRODUCT_VALUE_FOR_SUBTOPIC_SHOWED', value);
};

export const setProductValueForQuizShowedToLocalStorage = (
  value: string
): void => {
  localStorage.setItem('TUTOPIYA_PRODUCT_VALUE_FOR_QUIZ_SHOWED', value);
};

export const setLeadModalOpenToLocalStorage = (open: boolean): void => {
  localStorage.setItem('TUTOPIYA_LEAD_MODAL_OPEN', JSON.stringify(open));
};

export const setReleaseNoteShowedInHomeToLocalStorage = (
  version: string
): void => {
  localStorage.setItem('TUTOPIYA_RELEASE_NOTE_FOR_HOME_PAGE_SHOWED', version);
};

export const setReleaseNoteShowedInSubjectPageToLocalStorage = (
  version: string
): void => {
  localStorage.setItem(
    'TUTOPIYA_RELEASE_NOTE_FOR_SUBJECT_PAGE_SHOWED',
    version
  );
};

export const setBannerStatusToLocalStorage = (
  bannerName: string,
  status: boolean
): void => {
  const banners = getBannerStatusesFromLocalStorage();
  banners[bannerName] = status;
  localStorage.setItem('TUTOPIYA_BANNER_STATUS', JSON.stringify(banners));
};

export const setPdfTriggerMessageStatusToLocalStorage = (
  subTopicId: string,
  status: boolean
): void => {
  const subTopics = getPdfTriggerMessageStatusFromLocalStorage();
  subTopics[subTopicId] = status;
  localStorage.setItem(
    'TUTOPIYA_PDF_TRIGGER_MESSAGE_STATUS',
    JSON.stringify(subTopics)
  );
};

export const setMotivationalNudgeStatusToLocalStorage = (
  subTopicId: string,
  status: boolean
): void => {
  const nudgeStatuses = getMotivationalNudgeStatusFromLocalStorage();
  nudgeStatuses[subTopicId] = status;
  localStorage.setItem(
    'TUTOPIYA_MOTIVATIONAL_NUDGE_STATUS',
    JSON.stringify(nudgeStatuses)
  );
};

export const setSubTopicStatusToLocalStorage = (
  subTopicId: string,
  status: boolean
): void => {
  const subTopics = getSubTopicStatusFromLocalStorage();
  subTopics[subTopicId] = status;
  localStorage.setItem('TUTOPIYA_SUB_TOPIC_STATUS', JSON.stringify(subTopics));
};

export const setPracticeQuestionStatusToLocalStorage = (
  subTopicId: string,
  status: boolean
): void => {
  const practiceQuestions = getPracticeQuestionStatusFromLocalStorage();
  practiceQuestions[subTopicId] = status;
  localStorage.setItem(
    'TUTOPIYA_PRACTICE_QUESTION_STATUS',
    JSON.stringify(practiceQuestions)
  );
};

export const setDashboardSelectedCurriculumToLocalStorage = (
  curriculum: string
): void => {
  localStorage.setItem('TUTOPIYA_DASHBOARD_SELECTED_CURRICULUM', curriculum);
};

export const setOnboardingUserIdToLocalStorage = (id: string): void => {
  localStorage.setItem('TUTOPIYA_ONBOARDING_USER_ID', id);
};

export const setTutorEditModeToLocalStorage = (mode: boolean): void => {
  localStorage.setItem('TUTOPIYA_TUTOR_EDIT_MODE', JSON.stringify(mode));
};

export const setFeedbackModalCancelledToLocalStorage = (id: string): void => {
  localStorage.setItem('TUTOPIYA_FEEDBACK_MODAL_CANCELLED', id);
};

export const setAdminCoursesFiltersToLocalStorage = (
  filters: AdminCoursesFiltersData,
  isClasses: boolean
): void => {
  const key = isClasses
    ? 'TUTOPIYA_ADMIN_CLASSES_FILTERS'
    : 'TUTOPIYA_ADMIN_COURSES_FILTERS';
  localStorage.setItem(key, JSON.stringify(filters));
};

export const setLastCourseCreditReminderNotificationSentAtToLocalStorage = (
  courseId: string,
  tutorId: string
): void => {
  const reminders = getLastCourseCreditReminderNotificationSentAtFromLocalStorage();
  const newReminder: CourseCreditReminderData = {
    courseId,
    tutorId,
    timestamp: Date.now(),
  };

  // Filter out old entries for the same course and tutor
  const filteredReminders = reminders.filter(
    (reminder) =>
      !(reminder.courseId === courseId && reminder.tutorId === tutorId)
  );

  filteredReminders.push(newReminder);

  localStorage.setItem(
    'LAST_COURSE_CREDIT_REMINDER_NOTIFICATION_SENT_AT',
    JSON.stringify(filteredReminders)
  );
};

// REMOVE FROM LOCAL STORAGE
export const removeAuthTokenToLocalStorage = (): void => {
  localStorage.removeItem('TUTOPIYA_QUIZ_AUTH_TOKEN');
  removeCookie(COOKIE_NAMES.AUTH_TOKEN);
};

export const removeAuthUserToLocalStorage = (): void => {
  localStorage.removeItem('TUTOPIYA_QUIZ_AUTH_USER');
  removeCookie(COOKIE_NAMES.AUTH_USER);
};

export const removeUserMapperFromLocalStorage = (): void => {
  localStorage.removeItem('TUTOPIYA_USER_MAPPER');
};

export const removeClassFromLocalStorage = (): void => {
  localStorage.removeItem('TUTOPIYA_QUIZ_CLASS');
};

export const removeAdminPortalEditModeValueFromLocalStorage = (): void => {
  localStorage.removeItem('TUTOPIYA_ADMIN_EDIT_MODE_VALUE');
};

export const removeSlideViewsLimitFromLocalStorage = (): void => {
  localStorage.removeItem('TUTOPIYA_SLIDE_VIEWS_LIMIT');
};

export const removeLeadModalOpenFromLocalStorage = (): void => {
  localStorage.removeItem('TUTOPIYA_LEAD_MODAL_OPEN');
};

export const removeDashboardSelectedCurriculumFromLocalStorage = (): void => {
  localStorage.removeItem('TUTOPIYA_DASHBOARD_SELECTED_CURRICULUM');
};

export const removeOnboardingUserIdFromLocalStorage = (): void => {
  localStorage.removeItem('TUTOPIYA_ONBOARDING_USER_ID');
};

export const removeTutorEditModeFromLocalStorage = (): void => {
  localStorage.removeItem('TUTOPIYA_TUTOR_EDIT_MODE');
};

export const removeFeedbackModalCancelledFromLocalStorage = (): void => {
  localStorage.removeItem('TUTOPIYA_FEEDBACK_MODAL_CANCELLED');
};

export const removeAdminCoursesFiltersFromLocalStorage = (
  isClasses: boolean
): void => {
  const key = isClasses
    ? 'TUTOPIYA_ADMIN_CLASSES_FILTERS'
    : 'TUTOPIYA_ADMIN_COURSES_FILTERS';
  localStorage.removeItem(key);
};
